/**
 * Étape 3 — Vue d'ensemble des installations.
 *
 * L'utilisateur indique en 1 clic par section si elle existe sur son
 * exploitation (Oui / Non). Chaque "Oui" déclenche dynamiquement
 * l'apparition de l'étape dédiée (serres, élevage, stockage) entre cette
 * étape et l'étape "Travaux".
 *
 * UX clé : on présente les sections recommandées en haut selon l'orientation
 * choisie à l'étape 1, et les sections non-recommandées en bas, dépliable.
 */

import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Beef,
  CheckCircle2,
  Flower,
  Info,
  Warehouse,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import { findOrientation } from './agri-config';
import { agriStepCounterLabel, useAgriStore } from './useAgriStore';

interface SectionMeta {
  id: 'serres' | 'elevage' | 'stockage';
  icon: LucideIcon;
  label: string;
  sublabel: string;
  /** Brève description ce qu'on demandera dans l'étape dédiée. */
  willAsk: string[];
}

const SECTIONS: SectionMeta[] = [
  {
    id: 'serres',
    icon: Flower,
    label: 'Serres horticoles ou maraîchères',
    sublabel: 'Cultures abritées sous serre',
    willAsk: [
      'Type (maraîchère / horticole / mixte)',
      'Couverture (verre, polycarbonate, tunnel)',
      'Chauffée ou non + combustible',
      'Régulation climatique automatisée',
    ],
  },
  {
    id: 'elevage',
    icon: Beef,
    label: "Bâtiments d'élevage",
    sublabel: 'Bovins, porcins, volailles, ovins, caprins',
    willAsk: [
      "Types d'animaux + effectif",
      'Tank à lait + salle de traite (si laitier)',
      'Ventilation et chauffage du bâtiment',
    ],
  },
  {
    id: 'stockage',
    icon: Warehouse,
    label: 'Espace de stockage',
    sublabel: "Matériel, grains, fruits, vin… opérationnel ou commercial",
    willAsk: [
      'Que stockez-vous ? (multi-sélection)',
      'Réfrigéré ou non',
      'Surface totale',
    ],
  },
];

export function Step3InfraOverview() {
  const orientation = useAgriStore((s) => s.orientation);
  const serres = useAgriStore((s) => s.serres);
  const elevage = useAgriStore((s) => s.elevage);
  const stockage = useAgriStore((s) => s.stockage);
  const patchSerres = useAgriStore((s) => s.patchSerres);
  const patchElevage = useAgriStore((s) => s.patchElevage);
  const patchStockage = useAgriStore((s) => s.patchStockage);
  const goNext = useAgriStore((s) => s.goNext);
  const counter = useAgriStore(agriStepCounterLabel);

  const reveals = useMemo(() => {
    const meta = orientation ? findOrientation(orientation) : null;
    return meta?.reveals ?? { serres: false, elevage: false, stockage: true };
  }, [orientation]);

  // Sections "recommandées" d'abord (selon l'orientation)
  const recommendedSections = SECTIONS.filter(
    (s) => (s.id === 'serres' && reveals.serres) || (s.id === 'elevage' && reveals.elevage) || (s.id === 'stockage' && reveals.stockage),
  );
  const otherSections = SECTIONS.filter((s) => !recommendedSections.includes(s));

  function setHas(id: SectionMeta['id'], value: boolean) {
    if (id === 'serres') patchSerres({ has: value });
    if (id === 'elevage') patchElevage({ has: value });
    if (id === 'stockage') patchStockage({ has: value });
  }

  function getHas(id: SectionMeta['id']): boolean | null {
    if (id === 'serres') return serres.has;
    if (id === 'elevage') return elevage.has;
    return stockage.has;
  }

  // On peut continuer dès qu'au moins une section a reçu une réponse (oui ou non).
  const canContinue =
    serres.has !== null || elevage.has !== null || stockage.has !== null;

  const countYes = [serres.has, elevage.has, stockage.has].filter(
    (x) => x === true,
  ).length;

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">
        {counter} · Vue d'ensemble
      </p>
      <h2 className="mt-2 h-section">
        Quelles installations avez-vous&nbsp;?
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-500">
        Indiquez en un clic ce qui est présent sur votre exploitation. Pour
        chaque{' '}
        <strong className="text-ink">Oui</strong>, nous ouvrirons une étape
        dédiée pour poser des questions précises et vous proposer les bons
        travaux CEE.
      </p>

      {/* Sections recommandées */}
      {recommendedSections.length > 0 && (
        <div className="mt-6">
          <p className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-800">
            <Info className="h-3 w-3" strokeWidth={2.2} />
            Recommandé pour votre orientation
          </p>
          <div className="space-y-2.5">
            {recommendedSections.map((section) => (
              <InfraCard
                key={section.id}
                section={section}
                value={getHas(section.id)}
                onChange={(v) => setHas(section.id, v)}
                recommended
              />
            ))}
          </div>
        </div>
      )}

      {/* Autres sections */}
      {otherSections.length > 0 && (
        <div className="mt-5">
          {recommendedSections.length > 0 && (
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-stone-400">
              Autres installations possibles
            </p>
          )}
          <div className="space-y-2.5">
            {otherSections.map((section) => (
              <InfraCard
                key={section.id}
                section={section}
                value={getHas(section.id)}
                onChange={(v) => setHas(section.id, v)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Hint dynamique */}
      <AnimatePresence>
        {countYes > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: easeStripe }}
            className="mt-5 flex items-center gap-2 rounded-xl border border-brand-200 bg-brand-50/60 px-3.5 py-2.5"
          >
            <CheckCircle2
              className="h-4 w-4 shrink-0 text-brand-700"
              strokeWidth={2}
            />
            <p className="text-[12.5px] text-brand-900">
              Nous vous poserons des questions ciblées pour{' '}
              <strong>
                {countYes === 1 ? '1 section' : `${countYes} sections`}
              </strong>{' '}
              dans les étapes suivantes.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <Button
        variant="brand"
        size="xl"
        disabled={!canContinue}
        onClick={goNext}
        className="mt-7 w-full gap-2"
      >
        Continuer
        <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

/* ─── Carte de section ──────────────────────────────────── */

function InfraCard({
  section,
  value,
  onChange,
  recommended = false,
}: {
  section: SectionMeta;
  value: boolean | null;
  onChange: (v: boolean) => void;
  recommended?: boolean;
}) {
  const Icon = section.icon;
  const isYes = value === true;
  const isNo = value === false;
  const answered = value !== null;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border-2 transition-colors duration-150',
        isYes
          ? 'border-brand-300 bg-brand-50/40'
          : isNo
            ? 'border-stone-200 bg-stone-50/40'
            : recommended
              ? 'border-stone-200 bg-white shadow-[0_2px_8px_-4px_rgba(10,10,10,0.06)]'
              : 'border-stone-200 bg-white',
      )}
    >
      <div className="flex items-start gap-3 p-4">
        <span
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors',
            isYes
              ? 'bg-brand-100 text-brand-700'
              : 'bg-stone-100 text-stone-600',
          )}
        >
          <Icon className="h-5 w-5" strokeWidth={1.7} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[14.5px] font-semibold text-ink">
            {section.label}
          </p>
          <p className="mt-0.5 text-[12.5px] leading-relaxed text-stone-500">
            {section.sublabel}
          </p>
        </div>
      </div>

      <div className="flex items-stretch gap-0 border-t border-stone-200/70 bg-white/60">
        <YesNoBigButton
          variant="yes"
          selected={isYes}
          onClick={() => onChange(true)}
        />
        <span aria-hidden className="w-px bg-stone-200/70" />
        <YesNoBigButton
          variant="no"
          selected={isNo}
          onClick={() => onChange(false)}
        />
      </div>

      {/* Aperçu des questions */}
      <AnimatePresence>
        {answered && isYes && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: easeStripe }}
            className="overflow-hidden"
          >
            <div className="border-t border-brand-200/60 bg-brand-50/30 px-4 py-3">
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-brand-700">
                Nous vous demanderons …
              </p>
              <ul className="space-y-1">
                {section.willAsk.map((q) => (
                  <li
                    key={q}
                    className="flex items-start gap-2 text-[11.5px] leading-relaxed text-brand-900"
                  >
                    <ArrowRight
                      className="mt-0.5 h-3 w-3 shrink-0 text-brand-500"
                      strokeWidth={2.2}
                    />
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function YesNoBigButton({
  variant,
  selected,
  onClick,
}: {
  variant: 'yes' | 'no';
  selected: boolean;
  onClick: () => void;
}) {
  const Icon = variant === 'yes' ? CheckCircle2 : XCircle;
  const label = variant === 'yes' ? 'Oui' : 'Non';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex flex-1 items-center justify-center gap-1.5 px-3 py-3 text-[13px] font-semibold transition-all duration-150',
        selected
          ? variant === 'yes'
            ? 'bg-brand-500 text-white shadow-[inset_0_-2px_0_rgba(0,0,0,0.1)]'
            : 'bg-stone-700 text-white shadow-[inset_0_-2px_0_rgba(0,0,0,0.1)]'
          : 'text-stone-500 hover:bg-stone-100 hover:text-ink',
      )}
    >
      {selected && <Icon className="h-3.5 w-3.5" strokeWidth={2.4} />}
      {label}
    </button>
  );
}
